import type { components } from "../../../apis/gen";
import { BlockButton } from "../../../inJoy-components/Button/BlockButton";
import { IconButton } from "../../../inJoy-components/Button/IconButton";
import { Chips } from "../../../inJoy-components/Chips/Chips";
import { Icon } from "../../../inJoy-components/Icon/Icon";
import { isPC } from "../../../utils";
import { formatHeroDateTime } from "../../../utils/date-utils";
import {
	EVENT_STATES,
	type EventStateValue,
	formatCountdown,
} from "../useEventState";

type Event = components["schemas"]["Event"];

interface HeroActiveProps {
	event: Event;
	selectedSessionIdx: number | null;
	onAddSession: () => Promise<void>;
	canAddSession: boolean;
	eventState: EventStateValue;
	countdown: number;
	onShare: () => void;
	onReceiveResults: () => void;
	hasJoined: boolean;
}

const PLACEHOLDER_IMG = `${import.meta.env.VITE_APP_WEBSITE_URL}/img/event-page/game_placeholder_thumbnail.png`;

const HeroActive = ({
	event,
	selectedSessionIdx,
	onAddSession: _onAddSession,
	canAddSession: _canAddSession,
	eventState,
	countdown,
	onShare,
	onReceiveResults,
	hasJoined,
}: HeroActiveProps) => {
	// Determine background image: cover_img > placeholder
	const fallbackImg = event.cover_img || PLACEHOLDER_IMG;

	// Get display name and formatted time based on selection
	const selectedSession =
		selectedSessionIdx !== null ? event.sessions?.[selectedSessionIdx] : null;

	const displayName =
		selectedSessionIdx === null
			? event.name
			: selectedSession?.name || `Session ${selectedSessionIdx + 1}`;

	const displayTime = formatHeroDateTime(
		selectedSessionIdx === null
			? event.started_at
			: (selectedSession?.started_at ?? event.started_at),
	);

	// Check if there are highlights
	const hasHighlights =
		selectedSessionIdx === null
			? // Event view: check if any session has highlights
				(event.sessions?.some(
					(session) => session.highlights && session.highlights.length > 0,
				) ?? false)
			: // Session view: check if selected session has highlights
				(selectedSession?.highlights?.length ?? 0) > 0;

	return (
		<div className="relative flex flex-col items-center justify-between w-full h-[450px] pb-4 lg:pb-10 px-[calc(50vw-600px)] text-white">
			<img
				className="absolute top-[0] left-[0] w-full h-full object-cover"
				src={fallbackImg}
				alt={event.name || "Game placeholder"}
			/>
			<div
				className="absolute top-[0] left-[0] w-full h-full bg-[rgba(0,0,0,0.5)]"
				style={{
					backgroundImage:
						"linear-gradient(180deg, rgba(0, 0, 0, 0.00) 13.81%, rgba(0, 0, 0, 0.5) 100%)",
				}}
			></div>
			<div className="relative w-full h-full flex flex-col px-5 lg:px-0 pt-10 lg:pt-0">
				<div className="flex-1 flex items-center justify-center">
					{(
						[
							EVENT_STATES.WAITING_IMMINENT,
							EVENT_STATES.PLAYING_ENDGAME_COUNTDOWN,
						] as EventStateValue[]
					).includes(eventState) && (
						<p
							className={`text-[80px] leading-[96px] tracking-[-0.5px] font-bold ${eventState === EVENT_STATES.PLAYING_ENDGAME_COUNTDOWN ? "text-red-500" : "text-white"}`}
						>
							{formatCountdown(countdown)}
						</p>
					)}
				</div>
				<div className="flex items-end justify-between pb-1 lg:pb-0">
					<div className="game-info text-white">
						<div className="flex gap-x-2">
							<Chips
								type="Primary"
								size="Small"
								label={
									eventState === EVENT_STATES.WAITING_NORMAL
										? "Scheduled"
										: "Started"
								}
							/>
							{event.checked_in_at && (
								<Chips type="Blue" size="Small" label="Check-in" />
							)}
						</div>
						<p className="mt-4 text-sm font-medium">{displayTime}</p>
						<p className="mt-2 text-3xl lg:text-4xl font-medium">
							{displayName}
						</p>
						{(event.location || event.booker) && (
							<div className="mt-4 flex gap-5">
								{event.location && (
									<div className="flex items-center gap-1">
										<Icon iconName="map" size="Large" color="#999" />
										<p className="text-sm text-color-Grey500">
											{event.location}
										</p>
									</div>
								)}
								{event.booker && (
									<div className="flex items-center gap-1">
										<Icon iconName="user_s_fill" size="Large" color="#999" />
										<p className="text-sm text-color-Grey500">{event.booker}</p>
									</div>
								)}
							</div>
						)}
					</div>
					<div className="flex gap-3 items-center">
						<div className="hidden lg:inline-flex gap-3">
							{hasJoined ? (
								<>
									<BlockButton
										type="Primary"
										width="183px"
										icon=""
										size="Large"
										onClick={() => {
											if (
												selectedSessionIdx !== null &&
												event.sessions?.[selectedSessionIdx]?.sharelink
											) {
												window.open(
													event.sessions[selectedSessionIdx].sharelink,
													"_blank",
												);
											}
										}}
										disabled={!hasHighlights}
										loading={false}
										loadingIcon=""
									>
										Edit Highlight in App
									</BlockButton>
									<BlockButton
										type="Secondary"
										width="140px"
										icon="check_outline"
										size="Large"
										disabled={true}
										loading={false}
										loadingIcon=""
									>
										Subscribed
									</BlockButton>
								</>
							) : (
								<BlockButton
									type="Primary"
									width="200px"
									icon=""
									size="Large"
									onClick={onReceiveResults}
									disabled={false}
									loading={false}
									loadingIcon=""
								>
									Receive Game Results
								</BlockButton>
							)}
						</div>
						<IconButton
							type="TertiaryWhite"
							icon="share_outline"
							size={isPC() ? "Large" : "Small"}
							disabled={false}
							loading={false}
							loadingIcon=""
							onClick={onShare}
						/>
					</div>
				</div>
			</div>
			{hasJoined && (
				<div className="relative w-full pt-8 px-5 flex lg:hidden gap-x-3">
					<BlockButton
						type="Secondary"
						width="100%"
						icon="check_outline"
						size="Large"
						disabled={true}
						loading={false}
						loadingIcon=""
					>
						Subscribed
					</BlockButton>
					{selectedSessionIdx !== null && (
						<BlockButton
							type="Primary"
							width="100%"
							icon=""
							size="Large"
							onClick={() => {
								if (
									selectedSessionIdx !== null &&
									event.sessions?.[selectedSessionIdx]?.sharelink
								) {
									window.open(
										event.sessions[selectedSessionIdx].sharelink,
										"_blank",
									);
								}
							}}
							disabled={!hasHighlights}
							loading={false}
							loadingIcon=""
						>
							Edit Highlight
						</BlockButton>
					)}
				</div>
			)}
		</div>
	);
};

export { HeroActive };
